import type { Profile, Snapshot, Venture } from './models';

export function isAI(p: Profile) {
  return p.is_ai || p.id === 'ai-assistant' || p.id === 'venture-bot';
}
export function isPaid(p: Pick<Profile, 'subscriptionTier'>) {
  return !!p.subscriptionTier && p.subscriptionTier !== 'free';
}
export function maxCollaboratorRating(p: Pick<Profile, 'subscriptionTier'>) {
  const tier = p.subscriptionTier ?? 'free';
  if (['pro', 'enterprise', 'elite', 'agency', 'ultimate'].includes(tier)) return 5;
  if (['basic', 'starter'].includes(tier)) return 4;
  return 3;
}
export function fixedAIRating(p: Profile) {
  return p.ai?.level === 'Principal'
    ? 5
    : p.ai?.level === 'Senior'
      ? 4
      : p.ai
        ? 1 + (p.ai.seed % 3)
        : 3;
}
const outcome =
  /(?:[£$€]\s*[1-9][\d,.]*|[1-9][\d,.]*\s+(?:sales|paying customers|orders|subscribers))\b/i;
const delivered = /\b(built|delivered|launched|shipped|implemented|completed|sold|converted)\b/i;
const qualifiedOutcome = (text: string) =>
  outcome.test(text) && !/\b(no|not|lost|refund|hypothetical|goal|target)\b/i.test(text);

/** Deterministic Elo-style reputation, not a claim of verified revenue or actual model intelligence. */
export function reputationFor(s: Snapshot, p: Profile): NonNullable<Profile['reputation']> {
  if (isAI(p)) {
    const money = fixedAIRating(p);
    return {
      score: 1200 + (money - 3) * 200,
      money,
      upvotes: 0,
      downvotes: 0,
      evidenceCount: 0,
      kind: 'fixed',
    };
  }
  const humans = new Set(s.profiles.filter((x) => !isAI(x)).map((x) => x.id));
  const opinions = new Map<string, number[]>();
  let evidenceCount = 0;
  for (const c of s.contributions.filter((x) => x.contributor === p.id)) {
    const v = s.ventures.find((x) => x.id === c.ventureId);
    if (!v) continue;
    const text = `${c.title} ${c.description} ${c.evidence}`;
    const meaningful =
      (delivered.test(text) && text.trim().length >= 24) ||
      (c.quantity ?? 0) >= 2 ||
      !!s.commitments.find((t) => t.id === c.commitmentId && t.status === 'Completed');
    const reports = s.messages.filter(
      (m) =>
        [v.id, `boardroom-${v.id}`, `venturebot-${v.id}`].includes(m.channel) &&
        humans.has(m.author),
    );
    const working = qualifiedOutcome(text) || reports.some((m) => qualifiedOutcome(m.text));
    let affirmed = false;
    for (const [voter, value] of Object.entries(c.votes ?? {})) {
      if (voter === p.id || !humans.has(voter) || !v.members.includes(voter)) continue;
      const values = opinions.get(voter) ?? [];
      values.push(value === -1 ? -1 : meaningful ? 1 : 0.4);
      opinions.set(voter, values);
      if (value === 1) affirmed = true;
    }
    // Reported outcomes gain only a capped bonus after a human teammate affirms meaningful work.
    if (meaningful && working && affirmed) evidenceCount++;
  }
  let score = 1200,
    positive = 0,
    negative = 0;
  for (const [, values] of [...opinions.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    // One effective opinion per distinct person, even across many contribution records.
    const opinion = values.reduce((a, b) => a + b, 0) / values.length;
    if (opinion > 0) positive++;
    else if (opinion < 0) negative++;
    else continue;
    const agreement = opinion > 0 ? positive : negative;
    const k = 32 * Math.min(4, 2 ** (agreement / 6));
    const expected = 1 / (1 + 10 ** ((1200 - score) / 400));
    score += k * ((opinion + 1) / 2 - expected);
  }
  score = Math.round(Math.max(800, Math.min(1600, score + Math.min(150, evidenceCount * 30))));
  return {
    score,
    money: Math.max(1, Math.min(5, Math.round(3 + (score - 1200) / 200))),
    upvotes: positive,
    downvotes: negative,
    evidenceCount,
    kind: opinions.size ? 'earned' : 'provisional',
  };
}
export function canAccessCollaborator(s: Snapshot, viewer: Profile, target: Profile) {
  return reputationFor(s, target).money <= maxCollaboratorRating(viewer);
}
export function ventureSummary(v: Venture): NonNullable<Profile['ventureSummaries']>[number] {
  return {
    id: v.id,
    name: v.name,
    pitch: v.pitch || v.description.slice(0, 180),
    mission: v.mission || v.solution || v.pitch || v.description,
    description: v.description,
    industry: v.industry || 'Not specified',
    stage: v.stage,
    memberCount: v.members.length,
    websiteUrl: v.websiteUrl,
    logo: v.logo,
  };
}
export function withPublicProfiles(s: Snapshot): Snapshot {
  return {
    ...s,
    profiles: s.profiles.map((p) => ({
      ...p,
      reputation: reputationFor(s, p),
      ventureSummaries: s.ventures.filter((v) => v.members.includes(p.id)).map(ventureSummary),
    })),
  };
}
